import { Box, Stack } from "@mui/material"
import { useBoundContext } from "lib/@components/binding/use-bound-context"
import { Repeat } from "lib/@components/repeat"
import { If } from "lib/@components/switch"
import { InlineCompareButtons } from "routes/schedule/compare/inline-approval-buttons/inline-compare-buttons"
import { CompareButtonsBelow } from "slot-definitions"
import { replaceAllWith } from "lib/@components/slot/replace-all-with"
import { InlineRejectionEditor } from "./inline-rejection-editor"
import { ReasonComponentComparison } from "routes/schedule/edit/reason-component-comparison"
import { AuthoringReviewComparisonTypeEntry } from "./authoring-review-comparison-type-entry"
import { AuthoringReviewComparisonTypePlainEntry } from "./authoring-review-comparison-type-plain-entry"
import { AuthoringReviewAmendsBlock } from "./authoring-review-amends-block"
import { AuthoringReviewPriorRejection } from "./authoring-review-prior-rejection"
import { getTaskAmendsKey } from "../amends/lib/required-amends-helpers"
import { Frag } from "lib/@components/slot/frag"
import { AuthoringReviewMovementsEntry } from "./authoring-review-movements-entry"
import { normalizeAuthoringTask } from "./lib/authoring-task-lookup"

export function AuthoringReviewComparisonTypeContent({
    id,
    children,
    components,
    rendered,
    to,
    from,
    canShowReason,
    changeType,
}) {
    const { splitDetailsCompareButtons, splitIntroCompareButtons } = useBoundContext()

    // Differences tab renders the shared ComparisonType, which plugs the read-only banner; clear
    // it and render our editable rejection banner instead (this content mounts inside it, so the
    // replace runs after the shared plug).
    CompareButtonsBelow.usePlug(replaceAllWith(<InlineRejectionEditor />))

    if (id === "details" && splitDetailsCompareButtons) {
        return (
            <Repeat
                list={components}
                pass="component"
                item={
                    <AuthoringReviewComparisonTypeEntry
                        comparisonId={id}
                        rendered={rendered}
                        to={to}
                        from={from}
                        showInlineButtons
                    />
                }
            />
        )
    }

    if (id === "intro" && splitIntroCompareButtons) {
        return (
            <Repeat
                list={components}
                pass="component"
                item={
                    <AuthoringReviewComparisonTypeEntry
                        comparisonId={id}
                        rendered={rendered}
                        to={to}
                        from={from}
                        showInlineButtons
                    />
                }
            />
        )
    }

    if (id === "movements") {
        return (
            <Frag>
                <AuthoringReviewMovementsEntry component={components?.[0]} />
                <Stack spacing={2} mt={2}>
                    <AuthoringReviewPriorRejection amendsKey="movements" />
                    <AuthoringReviewAmendsBlock amendsKey="movements" subtitle="Change Task Order Reason" />
                    <InlineCompareButtons idFromGroup={id} to={to} from={from} rendered={rendered} />
                </Stack>
            </Frag>
        )
    }

    const normalizedTo = normalizeAuthoringTask(to)
    const normalizedFrom = normalizeAuthoringTask(from)
    const taskAmendsKey = getTaskAmendsKey(normalizedTo ?? normalizedFrom)
    if (!taskAmendsKey) {
        return children
    }

    return (
        <Frag>
            <Repeat list={components} pass="component" item={<AuthoringReviewComparisonTypePlainEntry />} />
            <Stack spacing={2} mt={2}>
                <AuthoringReviewPriorRejection amendsKey={taskAmendsKey} />
                <AuthoringReviewAmendsBlock amendsKey={taskAmendsKey} subtitle="Change Task Reason" />
                <If value={canShowReason}>
                    <Box mb={2} mx={3}>
                        <ReasonComponentComparison item={normalizedTo} changeType={changeType} />
                    </Box>
                </If>
                <InlineCompareButtons idFromGroup={id} to={normalizedTo} from={normalizedFrom} rendered={rendered} />
            </Stack>
        </Frag>
    )
}
